import React, { useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import styled from 'styled-components';
import setImageSize, { IsWidth1300, MaxWidthApp } from 'Utils/UtilityFunctions';
import {
  boxShadow,
  maxTablet,
  maxWidth, maxWidth1300, primaryFont, primaryHeadingColor,
  retina, secondaryFont, wBold, wMedium, youtubeIconPlay,
} from 'Utils/utilsStyle';
import VideoPopup from '../modal/VideoPopup';

const Wrapper = styled.div`
  box-shadow: ${boxShadow};
  margin-bottom: 13px;
  border-radius: 8px;
  .video-frame-area {
    min-width: 100%;
    height: 280px;
    width: auto;
    position: relative;
    cursor: pointer;
    @media all and (max-width: ${maxTablet}) {
      height: 160px;
    }
    @media all and (max-width: ${maxWidth1300}) and (min-width: ${maxTablet}) {
      height: 220px;
    }
    .lazy-load-image-background {
      height: 100%;
      width: 100%;
    }
    .layer {
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0px;
      left: 0px;
      border-radius: 8px 8px 0px 0px;
      transition: 0.5s ease all;
      &:hover {
        background-color: rgba(0,0,0,0.2);
      }
    }
    .youtube-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    .thumbnail-image {
     height: 100%;
     width: 100%;
     border-radius: 8px 8px 0px 0px;
    }
  }
  .text-area {
    padding: 12px 20px 14px 24px;
    background-color: #fff;
    border-radius: 0px 0px 8px 8px;
    border-top: 1px solid #DADADA;
    .heading {
      font-size: 18px;
      line-height: 26px;
      margin-bottom: 16px;
      color: ${primaryHeadingColor};
      font-family: ${secondaryFont};
      font-weight: ${wBold};
    }
    .bottom-tips {
      display: flex;
      align-items: center;
      .tips {
        font-size: 12px;
        line-height: 13px;
        font-family: ${primaryFont};
        font-weight: ${wBold};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        @media all and (max-width: ${maxWidth}) , ${retina} {
          font-size: 10px;
        }
        &.separator-left {
          border-left: 1.5px solid #7E7E7E;
          padding: 0px 5px;
          margin: 0px 5px;
        }
        &.domain {
          color: #DB3725;
        }
        &.date {
         color: #7E7E7E;
        }
      }
    }
  }
`;

const ListWrapper = styled.div`
  background-color: #fff;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  position: relative;
  height: 117px;
  padding: 16px 10px 16px 10px;
  box-shadow: ${boxShadow};
  margin-bottom: 8px;
  .video-frame-area {
    height: 85px;
    width: 85px;
    min-width: 85px;
    border-radius: 8px;
    margin-left: 5px;
    position: relative;
    .thumbnail-image {
      height: 100%;
      width: 100%;
      min-width: 100%;
      border-radius: 8px;
    }
    .layer {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0px;
      left: 0px;
    }
    .youtube-icon {
      height: 50px;
      width: 55px;
    }
  }
  .text-area {
    margin-left: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    .heading {
      font-family: ${secondaryFont};
      font-weight: ${wMedium};
      font-size: 16px;
      line-height: 26px;
      letter-spacing: -0.45px;
      color: ${primaryHeadingColor};
    }
    .footer-data-article {
      .bottom-tips {
        display: flex;
        .tips {
          font-family: ${primaryFont};
          font-weight: ${wMedium};
          font-size: 11px;
          line-height: 20px;
          &.domain {
            color: #DB3725;
            border-right: 1.5px solid #7E7E7E;
            padding: 0px 5px 0px 0px;
          }
          &.date {
            color: #7E7E7E;
            margin-left: 5px;
          }
        }
      }
    }
  }
`;

type Props = {
  videoLink?: string,
  videoTitle?: string,
  sourceName?: string,
  thumbnail?: string,
  time?: string,
  articleId?: number,
  articleWidth?: string,
  articleHeight?: string,
  listView?: boolean;
} & typeof defaultProps;

const defaultProps = {
  thumbnail: '',
  videoLink: '',
  videoTitle: '',
  sourceName: '',
  time: '',
  articleId: 0,
  articleWidth: '570',
  articleHeight: '280',
  listView: false,
};

const VideoTileGlobal = function VideoTileGlobal(props: Props) {
  const [visible, setVisible] = useState<boolean>(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const width1300 = IsWidth1300();
  const maxWidth1500 = MaxWidthApp();
  const {
    videoLink, videoTitle, sourceName, time, thumbnail,
    articleId, articleWidth, articleHeight, listView,
  } = props;
  const articleHeightLG = width1300 ? 220 : 160;
  const isVisible = () => {
    setVisible(!visible);
  };
  const articleHeightFn = () => (maxWidth1500 ? articleHeight : articleHeightLG);
  const articleWidthFn = () => (maxWidth1500 ? articleWidth : 370);
  return (
    listView ? (
      <ListWrapper>
        <div className="video-frame-area">
          <img
            src={setImageSize(
              thumbnail,
              85,
              85,
              articleId,
            )}
            className="thumbnail-image"
            alt="thumbnail"
          />
          <div
            className="layer"
            onClick={isVisible}
            onKeyDown={isVisible}
            tabIndex={0}
            role="button"
            aria-label="Play on youtube"
          >
            <img src={youtubeIconPlay} className="youtube-icon" alt="youtube-icon" />
          </div>
        </div>
        <div className="text-area">
          <h3 className="heading text-truncate-three-line">{videoTitle}</h3>
          <div className="footer-data-article">
            <div className="bottom-tips">
              <div className="tips domain">
                {sourceName}
              </div>
              <div className="tips date separator-left">{time}</div>
            </div>
          </div>
        </div>
        <VideoPopup videoLink={videoLink} visible={visible} setVisible={isVisible} />
      </ListWrapper>
    ) : (
      <Wrapper>
        <div className="video-frame-area" ref={elementRef}>
          <LazyLoadImage
            src={setImageSize(
              thumbnail,
              articleWidthFn(),
              articleHeightFn(),
              articleId,
            )}
            className="thumbnail-image"
            alt="thumbnail"
          />
          <div
            className="layer"
            onClick={isVisible}
            onKeyDown={isVisible}
            tabIndex={0}
            role="button"
            aria-label="Play on youtube"
          >
            <img src={youtubeIconPlay} className="youtube-icon" alt="youtube-icon" />
          </div>
        </div>
        <div className="text-area">
          <h3 className="heading text-truncate-three-line">{videoTitle}</h3>
          <div className="footer-data-article">
            <div className="bottom-tips">
              <div className="tips domain">
                {sourceName}
              </div>
              <div className="tips date separator-left">{time}</div>
            </div>
          </div>
        </div>
        <VideoPopup videoLink={videoLink} visible={visible} setVisible={isVisible} />
      </Wrapper>
    )
  );
};

VideoTileGlobal.defaultProps = defaultProps;
export default VideoTileGlobal;
